import { cva, type VariantProps } from 'class-variance-authority';
import type { LucideIcon } from 'lucide-react';
import { Slot } from 'radix-ui';
import * as React from 'react';

import { cn } from '../lib/utils';

const vButtonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-lg text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'text-foreground hover:bg-neutral-200/50',
        primary: 'bg-primary-600 text-on-primary hover:bg-primary-700',
        outline: 'border border-border bg-card text-foreground hover:bg-muted',
        ghost: 'text-muted-foreground hover:bg-neutral-200/50',
        destructive: 'bg-error-600 text-on-primary hover:bg-error-700',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        default: 'h-10 px-4',
        lg: 'h-11 px-6',
      },
      fullWidth: {
        true: 'w-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  },
);

type VButtonProps = React.ComponentPropsWithoutRef<'button'> &
  VariantProps<typeof vButtonVariants> & {
    /** Optional leading icon (Lucide). Renders at `size-4` before the children. */
    icon?: LucideIcon;
    /** When true, forwards Button styles to its single child via Radix Slot. Use to wrap `<Link>` etc. */
    asChild?: boolean;
  };

/**
 * Primary interactive control. Use for all button-like actions.
 * Variant reflects hierarchy: `primary` for CTAs, `outline` secondary, `ghost` tertiary, `destructive` for irreversible actions.
 */
export const VButton = React.forwardRef<HTMLButtonElement, VButtonProps>(
  function VButton(
    { className, variant, size, fullWidth, icon: Icon, asChild = false, children, ...props },
    ref,
  ) {
    const classes = cn(vButtonVariants({ variant, size, fullWidth, className }));

    if (asChild) {
      return (
        <Slot.Root ref={ref} className={classes} {...props}>
          {children}
        </Slot.Root>
      );
    }

    return (
      <button ref={ref} className={classes} {...props}>
        {Icon && <Icon className="size-4 shrink-0" />}
        {children}
      </button>
    );
  },
);
VButton.displayName = 'VButton';

export { vButtonVariants };
export type { VButtonProps };
